import styled, { keyframes } from "styled-components";

/*
 * Design tokens copied from the CSM Figma set (see pages/SystemPage/SystemPage.css) rather
 * than referenced through var(--primary) — the same thing ReportPage.styles.ts does, and for
 * the same reason with an extra edge here.
 *
 * SystemPage.css is the file that declares those variables on :root, and it is imported by
 * SystemPage.tsx, which is lazy-loaded. So on any screen where the user has not yet visited
 * System Settings, var(--primary) resolves to NOTHING. Every other page that uses the tokens
 * happens to be reached through the same manager ribbon, so it never shows; this panel can be
 * opened on EVERY screen, including the first one after login. Copying the values keeps it
 * correct regardless of load order, and they are the same hex values, so it still reads as one
 * app. (`SupportLauncherButton` hardcodes the same hex values for the same reason.)
 */
const tokens = `
  --sa-bg: #ffffff;
  --sa-bg-alt: #f9f9f9;
  --sa-text: #36424a;
  --sa-muted: #828a8f;
  --sa-border: rgba(196, 199, 200, 0.6);
  --sa-border-strong: #c4c7c8;
  --sa-primary: #004d9e;
  --sa-accent: #0091ba;
  --sa-radius-pill: 30px;
  --sa-radius-sm: 4px;
  --sa-font: 'Noto Sans', system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
`;

/**
 * How far the Submit Feedback drawer reaches in from the right, plus a gutter.
 *
 * ⚠️ Keep `420px` in step with `ModalBox`'s width in
 * `components/Modals/DiagnosticModal/DiagnosticModal.styles.ts`. Nothing links the two, and
 * getting it wrong doesn't break anything — the panel just sits under the drawer, which is
 * precisely the state this offset exists to avoid. It matters more than it did: the drawer
 * OPENS the panel now, so the two are on screen together as a matter of course rather than by
 * coincidence.
 */
const DRAWER_WIDTH = "420px";

/**
 * Sit clear of the feedback drawer, and ABOVE it.
 *
 * The drawer's overlay is `position: fixed; inset: 0` at `z-index: 6000` — it covers the
 * whole viewport, not just the 420px panel — so merely moving left is not enough. Anything
 * meant to stay clickable alongside it has to outrank the overlay too.
 */
/*
 * ...and TOP-ALIGNED with the drawer's form, so the two read as one grounded pair rather than a
 * panel floating at an arbitrary height beside it (2026-10-01 design review). 80px is the
 * drawer's first field: `ModalBox` padding-top 24 + its 32px header row + the 24px gap. Keep in
 * step with DiagnosticModal.styles.ts like `DRAWER_WIDTH`.
 */
const DRAWER_CONTENT_TOP = "80px";

const offsetForDrawer = `
  right: calc(${DRAWER_WIDTH} + 24px);
  z-index: 6100;
  top: ${DRAWER_CONTENT_TOP};
  bottom: auto;
  height: min(560px, calc(100vh - ${DRAWER_CONTENT_TOP} - 24px));
`;

/*
 * The launcher is `components/SupportAssistant/SupportLauncherButton` — a floating bubble the
 * panel renders at this same anchor while it is closed. It carries its own copy of the drawer
 * offset.
 */

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Panel = styled.div<{ $offset?: boolean }>`
  ${tokens}

  position: fixed;
  right: 24px;
  bottom: 72px;
  z-index: 900;

  display: flex;
  flex-direction: column;
  width: 380px;
  /* Bounded by the viewport so it can never grow past the screen on a short laptop. */
  height: min(560px, calc(100vh - 140px));

  /* After height, which the drawer offset overrides. */
  ${({ $offset }) => ($offset ? offsetForDrawer : "")}

  background: var(--sa-bg);
  border: 1px solid var(--sa-border);
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(54, 66, 74, 0.22);
  font-family: var(--sa-font);
  color: var(--sa-text);
  text-align: left;

  animation: ${riseIn} 140ms ease-out;

  @media (max-width: 720px) {
    right: 8px;
    left: 8px;
    bottom: 8px;
    width: auto;
    height: min(70vh, calc(100vh - 96px));
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid var(--sa-border);

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    /* The heading is the logo — it holds no text of its own, so the font rules above apply
       to nothing today. They are kept for the fallback case: if the image fails to load, the
       browser paints its alt text ("CoSMo") in their place rather than at heading defaults.
       No backticks in here: this is a template literal, and one would end it. */
    display: flex;
    align-items: center;
  }
`;

/**
 * THE NAME AT THE TOP OF THE PANEL IS THE LOGO, AND IT IS THE PNG DELIBERATELY.
 *
 * The lockup also exists as `cosmo-logo.svg`, and that file is NOT what is used here: its
 * wordmark is live `<text>` in the app's font stack, so the letterforms and the spacing
 * depend on Noto Sans having loaded. The PNG (712 × 256, transparent) renders the approved
 * artwork identically on every machine, which is the whole point of putting a logo rather
 * than the word "CoSMo" in this row.
 *
 * ⚠️ HEIGHT IS THE DIMENSION THAT IS SET, WIDTH FOLLOWS. Fixing both, or sizing by width,
 * is how a lockup gets stretched. 28px keeps the row the height the `<h2>` gave it and
 * leaves the "Clear chat" + close pair room on the 380px panel.
 *
 * ⚠️ IT MUST NOT BE GIVEN THE HEADER'S `user-icon` CLASS (or any other recolour filter).
 * That class — which the launcher button in `CSMShopfloorHeader` deliberately DOES use —
 * flattens an icon to one blue at 50% opacity, which would throw away the two-colour
 * wordmark this asset exists for: C, S and M in `#004D9E`, the o's in `#0091BA`.
 */
export const HeaderLogo = styled.img`
  display: block;
  height: 28px;
  width: auto;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border: none;
  border-radius: var(--sa-radius-sm);
  background: transparent;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 77, 158, 0.08);
  }

  img {
    width: 12px;
    height: 12px;
  }
`;

/**
 * "Clear chat". A quiet text button in the header, beside Close.
 *
 * Deliberately NOT an icon: a bin glyph next to an X is two small ambiguous targets on a
 * touch screen, and clearing a conversation is destructive in the one way that matters here
 * — the thread is the only copy. It is hidden outright while the thread is empty rather than
 * disabled, because a permanently greyed control on an otherwise empty panel reads as broken.
 */
export const ClearButton = styled.button`
  padding: 6px 10px;
  border: none;
  border-radius: var(--sa-radius-sm);
  background: transparent;
  color: var(--sa-muted);
  font-family: var(--sa-font);
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(0, 77, 158, 0.08);
    color: var(--sa-text);
  }
`;

/** Holds the header's two controls together so the title keeps the rest of the row. */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

/**
 * The thumbs under one answer.
 *
 * Aligned with the bubble rather than centred, and low-contrast until used: it is an
 * invitation, not a required step, and an operator who wants their answer and nothing else
 * should be able to ignore it without it looking unfinished.
 */
export const RateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  margin-top: -6px;
`;

export const RateButton = styled.button<{ $active?: boolean }>`
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid ${({ $active }) => ($active ? "var(--sa-primary)" : "transparent")};
  border-radius: var(--sa-radius-sm);
  background: ${({ $active }) => ($active ? "rgba(0, 77, 158, 0.10)" : "transparent")};
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 77, 158, 0.08);
  }

  /* An <img>, never an icon font — every Syncfusion e-icons glyph renders blank app-wide,
     which is why the ribbon and the launcher draw their icons this way too. */
  img {
    width: 15px;
    height: 15px;
    /* Quiet until the answer has been rated, so an unused control does not compete with the
       answer it sits under. */
    opacity: ${({ $active }) => ($active ? 1 : 0.55)};
  }

  &:hover img {
    opacity: 1;
  }
`;

export const Thread = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Intro = styled.p`
  margin: 0;
  color: var(--sa-muted);
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
`;

/**
 * A message in the thread.
 *
 * ⚠️ `white-space: pre-wrap` applies to the USER's bubbles only. What they typed is literal
 * text and their line breaks are theirs to keep. An assistant bubble is rendered by
 * `AnswerText`, which emits real paragraphs and lists — with `pre-wrap` still on, every
 * newline the model wrote would be honoured a SECOND time, on top of the block spacing, and
 * the answer would come out double-spaced with ragged gaps.
 */
export const Bubble = styled.div<{ $fromUser?: boolean }>`
  max-width: 88%;
  align-self: ${({ $fromUser }) => ($fromUser ? "flex-end" : "flex-start")};
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  white-space: ${({ $fromUser }) => ($fromUser ? "pre-wrap" : "normal")};
  overflow-wrap: anywhere;

  background: ${({ $fromUser }) => ($fromUser ? "rgba(0, 77, 158, 0.08)" : "var(--sa-bg-alt)")};
  border: 1px solid ${({ $fromUser }) => ($fromUser ? "rgba(0, 77, 158, 0.18)" : "var(--sa-border)")};

  strong {
    font-weight: 600;
  }
`;

/** A paragraph of an answer. First and last lose their margin so the bubble keeps its padding. */
export const AnswerBlock = styled.p`
  margin: 0 0 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const stepList = `
  margin: 0 0 10px;
  padding-left: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  li {
    margin-bottom: 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const OrderedSteps = styled.ol`
  ${stepList}
`;

export const UnorderedSteps = styled.ul`
  ${stepList}
`;

/**
 * A screenshot from the printed CSM guides, shown inline at the step it illustrates.
 *
 * Full bleed to the bubble's padding, because at ~350px a full-page screenshot of the manager
 * table needs every pixel it can get. It is still only enough to recognise the screen — which
 * is why the whole figure is a click target that opens it full size.
 */
export const AnswerFigure = styled.figure`
  ${tokens}

  margin: 4px 0 12px;
  cursor: zoom-in;

  &:last-child {
    margin-bottom: 0;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    border: 1px solid var(--sa-border-strong);
    border-radius: var(--sa-radius-sm);
    background: #ffffff;
  }

  figcaption {
    margin-top: 4px;
    color: var(--sa-muted);
    font-size: 12px;
    line-height: 16px;
  }
`;

/*
 * ---------------------------------------------------------------------------------------
 * The order card: one production order drawn inline, where the answer put it.
 *
 * It carries ${tokens} for the same reason everything else in this file does — SystemPage.css
 * declares the :root variables and is only imported by the lazy SystemPage, so on any screen
 * the user has not visited yet var(--sa-primary) resolves to nothing. This is drawn in the
 * panel, which is on every screen.
 *
 * Deliberately QUIET. It sits next to the sentence that says the same thing, so it is a
 * summary the eye can land on, not a competing headline — one accent rule down the side and
 * otherwise the panel's own greys. The one thing allowed to shout is the Late chip, because
 * that is the field somebody scanning the card is looking for.
 * ---------------------------------------------------------------------------------------
 */

export const Card = styled.section`
  ${tokens}

  margin: 4px 0 12px;
  padding: 10px 12px;
  border: 1px solid var(--sa-border);
  border-left: 3px solid var(--sa-primary);
  border-radius: var(--sa-radius-sm);
  background: var(--sa-bg-alt);
  font-family: var(--sa-font);
  color: var(--sa-text);

  &:last-child {
    margin-bottom: 0;
  }
`;

export const CardTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  /* An order number is data and can be long; it must not widen a 380px panel. */
  overflow-wrap: anywhere;
`;

export const CardSubtitle = styled.p`
  margin: 2px 0 0;
  color: var(--sa-muted);
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
  overflow-wrap: anywhere;
`;

export const CardState = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
`;

/** The status chips — on the board, being worked on, late. */
export const CardChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

/**
 * One status chip.
 *
 * `$tone` is "late" only for the one chip that is a warning. Everything else is informational,
 * and colouring it would make a card of ordinary facts read as a list of problems.
 */
export const CardChip = styled.span<{ $tone?: "late" }>`
  padding: 2px 8px;
  border: 1px solid
    ${({ $tone }) => ($tone === "late" ? "#b3261e" : "var(--sa-border-strong)")};
  border-radius: var(--sa-radius-pill);
  background: ${({ $tone }) => ($tone === "late" ? "rgba(179, 38, 30, 0.08)" : "transparent")};
  color: ${({ $tone }) => ($tone === "late" ? "#b3261e" : "var(--sa-muted)")};
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
`;

/**
 * The figures. Two columns at 380px; the labels are short enough that a third would leave no
 * room for the values, which are the part anybody is reading.
 */
export const CardFacts = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
  margin: 10px 0 0;

  dt {
    margin: 0;
    color: var(--sa-muted);
    font-size: 11px;
    font-weight: 400;
    line-height: 14px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  dd {
    margin: 0;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    overflow-wrap: anywhere;
  }
`;

/** One label/value pair, kept together so the grid wraps by field rather than by row. */
export const CardFact = styled.div`
  min-width: 0;
`;

/**
 * The enlarged screenshot.
 *
 * Fixed to the viewport rather than to the panel: the panel is 380px wide, so confining the
 * "full size" view to it would defeat the point. z-index clears the feedback drawer's overlay
 * (6000) for the same reason `Panel`'s `$offset` does.
 */
export const ImageOverlay = styled.div`
  ${tokens}

  position: fixed;
  inset: 0;
  z-index: 6200;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;

  background: rgba(0, 0, 0, 0.75);
  cursor: zoom-out;

  img {
    max-width: 100%;
    max-height: calc(100% - 40px);
    object-fit: contain;
    border-radius: var(--sa-radius-sm);
    background: #ffffff;
  }

  span {
    color: #ffffff;
    font-family: var(--sa-font);
    font-size: 13px;
    text-align: center;
  }
`;

/** The "I couldn't answer that" affordance. Deliberately quiet — it is an offer, not an error. */
export const EscalateRow = styled.div`
  align-self: flex-start;
  max-width: 88%;
`;

export const EscalateButton = styled.button`
  padding: 8px 16px;
  border: 1px solid var(--sa-border-strong);
  border-radius: var(--sa-radius-pill);
  background: transparent;
  color: var(--sa-text);
  font-family: var(--sa-font);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover {
    border-color: #36424a;
  }
`;

/**
 * The chips under one answer: screens to open, dialogs to open, forms to fill in, and
 * follow-up questions to ask.
 *
 * ⚠️ THEY SIT OUTSIDE THE BUBBLE, AND THAT IS A SECURITY DECISION, NOT A LAYOUT ONE.
 * `AnswerText`'s grammar is closed and has no way to express a destination — no links, no raw
 * HTML, and an `<img>` that can only be a base64 `data:` URI. A chip's destination is resolved
 * HERE, from a key, through `utility/routes.ts`. Rendering chips inside the answer would mean
 * teaching that renderer about destinations, which is the one thing it must never know.
 *
 * Wrapping is expected: the panel is 380px wide and an answer can carry several.
 */
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-self: flex-start;
  max-width: 100%;
  margin-top: -4px;
`;

/**
 * One chip. Quiet by default — these are offers, not instructions, and an answer that is
 * already correct must not be crowded out by a row of buttons competing with it.
 *
 * `$primary` marks the families that DO something to the app (open a screen, open a dialog,
 * open a form) apart from the follow-up, which only continues the conversation. The user
 * should be able to tell, before clicking, which kind of thing they are about to press.
 */
export const Chip = styled.button<{ $primary?: boolean }>`
  ${tokens}

  max-width: 100%;
  padding: 6px 12px;
  border: 1px solid ${({ $primary }) => ($primary ? "var(--sa-primary)" : "var(--sa-border-strong)")};
  border-radius: var(--sa-radius-pill);
  background: ${({ $primary }) => ($primary ? "rgba(0, 77, 158, 0.08)" : "transparent")};
  color: ${({ $primary }) => ($primary ? "var(--sa-primary)" : "var(--sa-text)")};

  font-family: var(--sa-font);
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  text-align: left;
  cursor: pointer;

  /* Long screen titles and follow-up questions must not force the panel to scroll sideways. */
  overflow-wrap: anywhere;
  transition: background-color 120ms ease, border-color 120ms ease;

  &:hover {
    background: rgba(0, 77, 158, 0.14);
    border-color: var(--sa-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/**
 * The strip above the composer, for chips about WHERE THE USER IS rather than about an answer.
 *
 * Only "Diagnose this order" today. It lives here, not in the thread, because it is available
 * before a single question has been asked — it is part of the panel's furniture on a screen
 * that has a production order, not a reply to anything.
 */
export const ContextRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px 0;
`;

export const Notice = styled.div`
  align-self: stretch;
  padding: 10px 12px;
  border-radius: var(--sa-radius-sm);
  background: var(--sa-bg-alt);
  border: 1px solid var(--sa-border);
  color: var(--sa-muted);
  font-size: 13px;
  font-weight: 300;
  line-height: 19px;
`;

export const Composer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid var(--sa-border);
`;

export const Input = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 9px 12px;
  border: 1px solid var(--sa-border-strong);
  border-radius: var(--sa-radius-sm);
  font-family: var(--sa-font);
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  color: var(--sa-text);
  resize: none;

  &::placeholder {
    color: var(--sa-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--sa-primary);
  }
`;

export const SendButton = styled.button`
  padding: 10px 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--sa-radius-pill);
  background: var(--sa-primary);
  color: #ffffff;
  font-family: var(--sa-font);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  &:disabled {
    background: var(--sa-border-strong);
    cursor: default;
  }
`;

export const Disclaimer = styled.p`
  margin: 0;
  padding: 0 12px 12px;
  color: var(--sa-muted);
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
`;
